import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { AutomationMethods } from "enums/Methods";
import { NumberField } from "features/WorklistTools/shared/components/NumberField";
import { SelectedSystem } from "features/WorklistTools/shared/components/SelectedSystem";
import { useAppReduxDispatch, useAppReduxSelector } from "hooks/reduxHooks";
import { useConfirm } from "state-provider/ConfirmDialogProvider/hooks";
import styled from "styled-components";
import { anyPlatesNot96Well } from "../../NucleofectionToolHelpers";
import {
  DestPlateInfoState,
  NucleofectionPlateInfoState,
  NucleofectionToolActions,
} from "../../state";
import { useHandleMethodSettingsUpdated } from "../../state/handlers";
import { useNucleofectionStateProvider } from "../../state/StateProvider";
import { DestPlateTransferStrategy } from "./components/DestPlateTransferStrategy";
import { NucleofectionTopUpVol } from "./components/NucleofectionTopUpVol";

const StyledMethodSettingsContainer = styled.div`
  height: auto;
  overflow: auto;
`;

const StyledMethodSettingsContentContainer = styled.div`
  display: grid;
  grid-template: auto / 1fr;
  grid-gap: 15px;
  margin-top: 5px;
  margin-right: 15px;
`;

export const MethodSettings = ({
  compoundPlateTypeInfo,
  destSampleTypes,
}: any) => {
  const { nucleofectionInternalState } = useNucleofectionStateProvider();
  const methodSettings = nucleofectionInternalState.methodSettings;
  const dispatch = useAppReduxDispatch();
  const updateMethodSettings = useHandleMethodSettingsUpdated();
  const { isConfirmed } = useConfirm();

  const nucleofectionState = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present
  );
  const compoundPlateInfo = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.compoundPlateInfo
  );
  const nucPlateInfo: NucleofectionPlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.nucPlateInfo
  );
  const destPlateInfo: DestPlateInfoState[] = useAppReduxSelector(
    (state) => state.WorklistTools.NucleofectionTool.present.destPlateInfo
  );

  const handleNucPlateInfoChanged = (key: string, value: any) => {
    dispatch(
      NucleofectionToolActions.UPDATE_NUC_PLATE_INFO({
        index: 0,
        key,
        value,
      })
    );
  };

  const handleSystemChanged = async (systemId: number, asamId: number) => {
    if (
      destPlateInfo.some(
        (e) =>
          e.labwareTypeCode.includes("_12_") ||
          e.labwareTypeCode.includes("_24_")
      )
    ) {
      const confirmed = await isConfirmed(
        "Some systems do not support use of 12 or 24-well plates. Are you sure you want to continue?"
      );

      if (confirmed) {
        updateMethodSettings("arraySystemAutomationMethodId", asamId);
        updateMethodSettings("selectedSystem", systemId);
      }
    } else {
      updateMethodSettings("arraySystemAutomationMethodId", asamId);
      updateMethodSettings("selectedSystem", systemId);
    }
  };

  const handleDestTransferStrategyChanged = (key: string, value: string) => {
    updateMethodSettings(key, value);
    if (value === "Custom") {
      for (const plate of destPlateInfo) {
        dispatch(
          NucleofectionToolActions.UPDATE_DEST_PLATE_INFO({
            index: plate.index,
            key: "transferVol",
            value: "",
          })
        );
      }
    }
  };

  const disableDestPlateTransferVol = anyPlatesNot96Well(destPlateInfo);

  return (
    <StyledMethodSettingsContainer>
      <StyledMethodSettingsContentContainer>
        <SelectedSystem
          automationMethodId={AutomationMethods.Nucleofection}
          selectedSystem={methodSettings.selectedSystem}
          handleSystemChanged={({ systemId, asamId }) => {
            handleSystemChanged(systemId, asamId);
          }}
        />
        <NumberField
          style={{ width: `100%` }}
          id="cells-reagent-vol"
          label={
            <Box sx={{ display: "inline-flex", alignItems: "center" }}>
              Compound Transfer Vol (µL)
              <Tooltip
                arrow
                placement="top"
                title="Compound reagent volume transferred from Source to Nucleofection Plate wells"
              >
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          }
          value={parseInt(compoundPlateInfo[0].transferVol)}
          onChange={() => {
            return;
          }}
          disabled
        />
        <NucleofectionTopUpVol
          item={nucPlateInfo[0]}
          handleNucPlateInfoChanged={handleNucPlateInfoChanged}
        />
        <DestPlateTransferStrategy
          handleDestTransferStrategyChanged={handleDestTransferStrategyChanged}
          isDisabled={disableDestPlateTransferVol}
        />
        <FormControl
          variant="outlined"
          fullWidth
          error={!methodSettings.destSampleType}
        >
          <InputLabel id="dest-sample-type-label">
            Destination Plate Sample Type
          </InputLabel>
          <Select
            labelId="dest-sample-type-label"
            label="Destination Plate Sample Type"
            id="dest-sample-type"
            value={methodSettings.destSampleType}
            onChange={(e) =>
              updateMethodSettings("destSampleType", e.target.value)
            }
          >
            <MenuItem value="None">No Sample Type Change</MenuItem>
            {destSampleTypes?.sampleTypes?.map((item: any) => (
              <MenuItem value={item.sampleTypeAbbreviation}>
                {item.sampleTypeString} ({item.sampleTypeAbbreviation})
              </MenuItem>
            ))}
          </Select>
          {!methodSettings.destSampleType ? (
            <FormHelperText>Required</FormHelperText>
          ) : null}
        </FormControl>
      </StyledMethodSettingsContentContainer>
    </StyledMethodSettingsContainer>
  );
};
